import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withClientConfig, SoyuzExperiment } from '@spotahome/soyuz/client';
import Metrics from '@spotahome/soyuz-metrics';

import '@spotahome/ui-library/lib/components/ImageCarousel/ImageCarousel.css';
import '@spotahome/ui-library/lib/components/ImageCarousel/SliderTrackImage.css';
import '@spotahome/ui-library/lib/components/ImageCarousel/GalleryArrow.css';

import { getRentableUnitPrice } from '@common/utils/paymentsAdapter';
import isFeatureDateToggleEnabled from '@common/utils/featureDateToggle';

import hideCancellationPoliciesShape from '@sru/shapes/hideCancellationPolicies';

import withFavorites from '../withFavorites';
import {
  getHomecardImageSet,
  getRatingFromReviews,
  getUrlWithMovingDates,
  getLabels
} from '../../utils/homecards';

import Homecard from './Homecard';

import { generateHomeCardLabels } from './generateHomeCardLabels';

const getHomecardClasses = (
  visited,
  selected,
  tracking,
  isMinimalRoundedCard
) =>
  classNames(
    'home-card',
    'is-animated',
    {
      'home-card--visited': visited,
      'home-card--selected': selected && !isMinimalRoundedCard,
      'home-card--minimal': isMinimalRoundedCard,
      'home-card--minimal-selected': selected && isMinimalRoundedCard
    },
    tracking
  );

class HomeCard extends Component {
  shouldComponentUpdate(nextProps) {
    return (
      this.props.selected !== nextProps.selected ||
      this.props.isFavorite !== nextProps.isFavorite ||
      this.props.freeCancellationDeadline !== nextProps.freeCancellationDeadline
    );
  }

  getHomecardProps = () => {
    const {
      data: ad,
      disabled,
      handleFavorite,
      isFavorite,
      moveIn,
      moveOut,
      flexibleMoveIn,
      flexibleMoveOut,
      onClick,
      overflow,
      selected,
      noSecurityDeposit,
      freeCancellationDeadline,
      clientConfig: {
        featureToggle: { hideCancellationPolicies, seasonalPriceRange }
      },
      renderExtraContent,
      isMinimalRoundedCard,
      features,
      showBillsIncludedLabel,
      showAvailableFrom,
      showPrice,
      newTab
    } = this.props;

    const { photoUrls } = ad;
    const adUrl = getUrlWithMovingDates(ad.url, moveIn, moveOut);
    const { data } = this.props;
    const hasPriceRangeToggle = seasonalPriceRange;

    const price = hasPriceRangeToggle
      ? getRentableUnitPrice(
          data.monthlyPrice.type,
          data.monthlyPrice.minimumPrice,
          data.currencyIsoCode,
          data.monthlyPrice.pricesByMonth,
          data.monthlyPrice.fixedPrice,
          hasPriceRangeToggle,
          { moveIn, moveOut },
          { flexibleMoveIn, flexibleMoveOut }
        )
      : {
          type: data.monthlyPrice.type,
          amount: data.monthlyPrice.minimumPrice,
          currencyCode: data.currencyIsoCode,
          isSeasonalPriceFeatureOn: hasPriceRangeToggle
        };

    return {
      disabled, // TODO: this might be treated in the patternLibrary
      favourite: isFavorite, // TODO: review if necessary
      availableFrom: ad.firstAvailableDate,
      handleFavourite: handleFavorite,
      image: ad.mainPhotoUrl,
      imageSet: photoUrls ? getHomecardImageSet(photoUrls) : '',
      isFavorite,
      link: adUrl,
      onClick,
      onMouseEnter: this.handleMouseEnter, // TODO: this might be treated in the patternLibrary
      onMouseLeave: this.handleMouseLeave, // TODO: this might be treated in the patternLibrary
      overflow,
      price,
      rating: getRatingFromReviews(ad.reviews),
      renderLabels: generateHomeCardLabels(ad),
      selected, // TODO: this might be treated in the patternLibrary
      title: ad.title,
      type: ad.type,
      verified: ad.checked,
      labels: getLabels(
        noSecurityDeposit,
        !isFeatureDateToggleEnabled(hideCancellationPolicies) &&
          freeCancellationDeadline.freeCancellationCurrently
          ? freeCancellationDeadline
          : null,
        features,
        showBillsIncludedLabel
      ),
      hasLazyLoad: this.props.hasLazyLoad,
      isSelect: ad.isSelect,
      renderExtraContent,
      isMinimalRoundedCard,
      showAvailableFrom,
      showPrice,
      newTab
    };
  };

  handleMouseEnter = () => {
    this.props.onCardMouseEnter(this.props.data.id);
  };

  handleMouseLeave = () => {
    this.props.onCardMouseLeave(this.props.data.id);
  };

  onClickEvent = eventType => {
    return Metrics.ga.sendEvent(
      'Search',
      `homecard slider ${eventType} chevron`,
      'click button'
    );
  };

  render() {
    const { data: ad, selected, tracking, isMinimalRoundedCard } = this.props;

    const classes = getHomecardClasses(
      ad.visited,
      selected,
      tracking,
      isMinimalRoundedCard
    );

    return (
      <div
        className={classes}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <SoyuzExperiment name="SearchChevronsSpeed">
          {experiment =>
            experiment.get('variant') === 'slider' ? (
              <Homecard photos={ad.photos} {...this.getHomecardProps()} />
            ) : (
              <Homecard {...this.getHomecardProps()} zoomOnHover />
            )
          }
        </SoyuzExperiment>
      </div>
    );
  }
}

HomeCard.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    adId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    checked: PropTypes.bool,
    isSuperlister: PropTypes.bool,
    isSelect: PropTypes.string,
    city: PropTypes.string,
    id: PropTypes.string,
    mainPhotoUrl: PropTypes.string,
    monthlyPrice: PropTypes.shape({
      type: PropTypes.string,
      minimumPrice: PropTypes.number,
      pricesByMonth: PropTypes.shape({
        [PropTypes.string]: PropTypes.number
      }),
      fixedPrice: PropTypes.number
    }),
    currencyIsoCode: PropTypes.string,
    photos: PropTypes.shape({ [PropTypes.string]: PropTypes.string }),
    photoUrls: PropTypes.shape({
      homecardHidpi: PropTypes.string,
      homecard: PropTypes.string
    }),
    reviews: PropTypes.shape({
      ratingAverage: PropTypes.number
    }),
    title: PropTypes.string,
    type: PropTypes.string,
    url: PropTypes.string,
    visited: PropTypes.bool
  }).isRequired,
  disabled: PropTypes.bool,
  handleFavorite: PropTypes.func.isRequired,
  noSecurityDeposit: PropTypes.bool,
  freeCancellationDeadline: PropTypes.shape({
    freeCancellationCurrently: PropTypes.bool,
    freeCancellationDeadline: PropTypes.string
  }),
  isFreeCancellationVariant: PropTypes.bool,
  isFavorite: PropTypes.bool.isRequired,
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  flexibleMoveIn: PropTypes.bool,
  flexibleMoveOut: PropTypes.bool,
  onCardMouseEnter: PropTypes.func,
  onCardMouseLeave: PropTypes.func,
  onClick: PropTypes.func,
  overflow: PropTypes.bool,
  selected: PropTypes.bool,
  tracking: PropTypes.string,
  clientConfig: PropTypes.shape({
    featureToggle: PropTypes.shape({
      seasonalPriceRange: PropTypes.bool,
      homecardSliderEnabled: PropTypes.bool,
      hideCancellationPolicies: PropTypes.bool,
      ...hideCancellationPoliciesShape
    })
  }).isRequired,
  hasLazyLoad: PropTypes.bool,
  renderExtraContent: PropTypes.func,
  isMinimalRoundedCard: PropTypes.bool,
  features: PropTypes.shape({ [PropTypes.string]: PropTypes.bool }).isRequired,
  showBillsIncludedLabel: PropTypes.bool,
  showAvailableFrom: PropTypes.bool,
  newTab: PropTypes.bool,
  showPrice: PropTypes.bool,
  zoomOnHover: PropTypes.bool
};

HomeCard.defaultProps = {
  disabled: false,
  moveIn: '',
  moveOut: '',
  flexibleMoveIn: false,
  flexibleMoveOut: false,
  onCardMouseEnter: () => {},
  onCardMouseLeave: () => {},
  onClick: () => {},
  overflow: false,
  selected: false,
  tracking: '',
  noSecurityDeposit: false,
  freeCancellationDeadline: {},
  isFreeCancellationVariant: false,
  hasLazyLoad: false,
  renderExtraContent: () => {},
  isMinimalRoundedCard: false,
  showBillsIncludedLabel: false,
  showAvailableFrom: true,
  newTab: true,
  showPrice: true,
  zoomOnHover: false
};

export default withClientConfig()(withFavorites()(HomeCard));
